/**
 * The v2 envelope card (TMMAPP-0151 §F 18; prototype P:553–563, visual contract v2 §6): `surface`
 * (the prototype's `surface2` would put the bars on an unguaranteed pair), radius 16, a 1 px `line`
 * border (`lineStrong` under the pointer), padding 16. It
 * holds the name (600) and the kind chip, the balance in `figureSm` (`text`, `moneyNegative` below
 * zero) in the owner's currency, the twelve served month-end bars, and the target line with its bar.
 * The whole card opens the envelope: the name is a stretched link, and the reorder handle and the
 * kebab (on the virtual account page) sit above it.
 *
 * The unassigned pool is the same card with no kind, no bars and no target: "Money not yet given a
 * job".
 */
import type { ReactNode } from 'react';
import { Box, Link, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { MoneyText } from '@/components/MoneyText';
import { ProgressTrack } from '@/components/ProgressTrack';
import { ReorderHandle, type ReorderHandleProps } from '@/components/SortableCardGrid';
import { TintChip } from '@/components/TintChip';
import { resolveTokens } from '@/theme/theme';
import { labelCanvasSx } from '@/theme/textLabel';
import { BalanceBars } from './BalanceBars';
import type { EnvelopeTileContent } from './envelopeTileContent';

interface EnvelopeTileProps {
  content: EnvelopeTileContent;
  /** Where the card opens; with `onOpen` instead, the name is a button. */
  to?: string;
  onOpen?: () => void;
  /** The open button's accessible name, when the name alone would be ambiguous. */
  openLabel?: string;
  currency: string | null;
  menu?: ReactNode;
  reorder?: ReorderHandleProps | null;
  /** The name is the card's `h3` (an envelope); false for the unassigned pool, which is no envelope. */
  heading?: boolean;
}

export function EnvelopeTile({
  content,
  to,
  onOpen,
  openLabel,
  currency,
  menu,
  reorder = null,
  heading = true,
}: EnvelopeTileProps) {
  const tokens = resolveTokens(useTheme());
  const label = tokens.textLabel('surface');
  const pct =
    content.progressBp === null ? 0 : Math.min(100, Math.max(0, content.progressBp / 100));
  const stretched = {
    color: tokens.text,
    fontWeight: 600,
    fontSize: 15,
    lineHeight: '21px',
    overflowWrap: 'anywhere',
    textAlign: 'start',
    // The whole card is the target; the handle and the menu sit above it.
    '&::after': { content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit' },
    '&:focus-visible': { outline: 'none' },
    '&:focus-visible::after': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
  } as const;
  return (
    <Box
      component="article"
      data-envelope-tile=""
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
        boxSizing: 'border-box',
        p: reorder ? '24px 16px 16px' : '16px',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: tokens.line,
        bgcolor: tokens.surface,
        color: tokens.text,
        ...labelCanvasSx(tokens, 'surface'),
        '&:hover': { borderColor: tokens.lineStrong },
      }}
    >
      {reorder && <ReorderHandle {...reorder} corner />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* An envelope's name is its card's heading (h3, under the panel's h2); the pool's is not. */}
        <Box
          component={heading ? 'h3' : 'span'}
          sx={{ flex: '1 1 auto', minWidth: 0, m: 0, font: 'inherit', display: 'block' }}
        >
          {to !== undefined ? (
            <Link component={RouterLink} to={to} underline="none" data-tile-name="" sx={stretched}>
              {content.name}
            </Link>
          ) : (
            <Box
              component="button"
              type="button"
              data-tile-name=""
              onClick={onOpen}
              {...(openLabel !== undefined ? { 'aria-label': openLabel } : {})}
              sx={{
                ...stretched,
                p: 0,
                border: 0,
                background: 'none',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              {content.name}
            </Box>
          )}
        </Box>
        {content.kind !== null && <TintChip tint={content.kind.tint} label={content.kind.label} />}
        {menu !== undefined && (
          <Box
            sx={{ position: 'relative', zIndex: 1, marginBlock: '-4px', marginInlineEnd: '-6px' }}
          >
            {menu}
          </Box>
        )}
      </Box>
      <Box sx={{ lineHeight: 1.1 }}>
        <MoneyText
          minor={content.balanceMinor}
          currency={currency}
          variant="figureSm"
          component="span"
        />
      </Box>
      {content.series !== null && content.series.length > 0 && (
        <BalanceBars series={content.series} currency={currency} height={34} />
      )}
      {content.progressBp !== null && (
        // The shared filled track (TMMAPP-0156 item 4), its fill by state (TMMAPP-0154 item 13);
        // decorative: the target line under it says the share.
        <ProgressTrack value={pct} tone={content.progressState} height={5} />
      )}
      <Typography component="span" sx={{ fontSize: 12.5, lineHeight: '18px', color: label }}>
        {content.line}
      </Typography>
    </Box>
  );
}
